import {
	ActionButton,
	Button,
	ProgressCircle,
	StatusLight,
} from '@react-spectrum/s2';
import ListBulleted from '@react-spectrum/s2/icons/ListBulleted';
import ListNumbered from '@react-spectrum/s2/icons/ListNumbered';
import TextBold from '@react-spectrum/s2/icons/TextBold';
import TextItalic from '@react-spectrum/s2/icons/TextItalic';
import { type Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Markdown, type MarkdownStorage } from 'tiptap-markdown';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { logger } from '@/logger';
import { cn } from '@/utils/cn';

// A reusable editable-markdown document surface: TipTap StarterKit + a formatting bar,
// markdown as the source of truth, and explicit Save / Cancel. Saving is deliberately NOT
// on blur: clicking the toolbar, scrolling out, or tabbing away all blur the editor, so a
// blur-save writes at moments the owner never meant as "done" and gives them no way back.
// Cancel restores the last saved markdown. The per-document seams (endpoint, testIds, error
// copy, height) are props.

const getMarkdown = (editor: Editor): string =>
	(
		editor.storage as unknown as { markdown: MarkdownStorage }
	).markdown.getMarkdown();

// Counters Tailwind preflight's reset of ul/ol/headings (mirrors EmailBodyEditor).
const CONTENT_CLASS = cn(
	'min-h-full text-sm leading-relaxed text-(--text-primary) outline-none',
	'[&_h1]:mt-2 [&_h1]:mb-1 [&_h1]:text-base [&_h1]:font-semibold',
	'[&_h2]:mt-3 [&_h2]:mb-1 [&_h2]:text-sm [&_h2]:font-semibold',
	'[&_h3]:mt-2 [&_h3]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold',
	'[&_p]:my-2 [&_strong]:font-semibold [&_em]:italic',
	'[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6',
	'[&_li]:leading-relaxed',
);

// Whether the document on screen matches what's stored. Tracked separately from the
// in-flight save so an edit made DURING a save is still reported as unsaved.
type SaveState = 'idle' | 'dirty' | 'saved' | 'error';

const SAVE_LABEL: Record<SaveState, string> = {
	idle: 'Saved',
	dirty: 'Unsaved changes',
	saved: 'Saved',
	error: 'Save failed — try again',
};
const SAVE_VARIANT: Record<SaveState, 'positive' | 'neutral' | 'negative'> = {
	idle: 'positive',
	dirty: 'neutral',
	saved: 'positive',
	error: 'negative',
};

type DocResponse = {
	markdown: string;
	updatedAt: string | null;
	edited: boolean;
};

export interface MarkdownDocEditorProps {
	// REST paths (already user-scoped by the caller) for GET (load/seed) and PATCH (save).
	getPath: string;
	patchPath: string;
	// Prefix for the doc/editor/error/save-state data-testids so multiple instances don't
	// collide and can be targeted uniquely in tests/E2E.
	testIdPrefix: string;
	// Copy shown when the document fails to load.
	loadErrorCopy: string;
	// Optional extra content rendered at the right of the toolbar, after Save/Cancel.
	toolbarAction?: ReactNode;
	// Optional content rendered above the editor (e.g. a provenance chip / disclosure line).
	header?: ReactNode;
	// Optional max-height class for the scroll region. Omit to fill the available height.
	scrollClassName?: string;
}

export function MarkdownDocEditor(props: MarkdownDocEditorProps) {
	const bridgewayApi = useBridgewayApi();
	const [markdown, setMarkdown] = useState<string | null>(null);
	const [loadError, setLoadError] = useState(false);
	// Bumped by "Try again" to re-run the fetch below.
	const [loadAttempt, setLoadAttempt] = useState(0);
	const { getPath, testIdPrefix, loadErrorCopy } = props;

	useEffect(() => {
		let cancelled = false;
		void bridgewayApi
			.get<DocResponse>(getPath)
			.then((data) => {
				if (cancelled) return;
				setMarkdown(data.markdown ?? '');
				setLoadError(false);
			})
			.catch((err) => {
				if (cancelled) return;
				logger.error('Failed to load markdown doc', err);
				setLoadError(true);
			});
		return () => {
			cancelled = true;
		};
	}, [bridgewayApi, getPath, loadAttempt]);

	if (loadError) {
		return (
			<div
				className="glass-card-sm p-6 text-center"
				data-testid={`${testIdPrefix}-error`}
			>
				<div className="flex flex-col items-center gap-4">
					<p className="text-sm text-(--text-primary)">{loadErrorCopy}</p>
					<Button
						variant="accent"
						size="S"
						onPress={() => {
							setLoadError(false);
							setLoadAttempt((n) => n + 1);
						}}
					>
						Try again
					</Button>
				</div>
			</div>
		);
	}
	if (markdown === null) {
		return (
			<div className="flex flex-1 items-center justify-center py-16">
				<ProgressCircle isIndeterminate aria-label="Loading" />
			</div>
		);
	}
	return <DocEditor initialMarkdown={markdown} {...props} />;
}

function DocEditor({
	patchPath,
	testIdPrefix,
	toolbarAction,
	header,
	scrollClassName,
	initialMarkdown,
}: MarkdownDocEditorProps & { initialMarkdown: string }) {
	const bridgewayApi = useBridgewayApi();
	const [saveState, setSaveState] = useState<SaveState>('idle');
	const [saving, setSaving] = useState(false);
	// The last markdown known to be persisted — what Cancel restores.
	const savedRef = useRef(initialMarkdown);

	const editor = useEditor({
		immediatelyRender: false,
		extensions: [
			StarterKit,
			Markdown.configure({ html: false, transformPastedText: true }),
		],
		content: initialMarkdown,
		editorProps: {
			attributes: {
				class: CONTENT_CLASS,
				'data-testid': `${testIdPrefix}-editor`,
			},
		},
		onUpdate: () => setSaveState('dirty'),
	});

	const save = async () => {
		if (!editor || saving) return;
		setSaving(true);
		const md = getMarkdown(editor);
		try {
			await bridgewayApi.patch(patchPath, { markdown: md });
			savedRef.current = md;
			// Read the editor OUTSIDE the state updater: switching tabs destroys it while the
			// PATCH is in flight, and a throw in there would report a save that SUCCEEDED as a
			// failure. An edit made during the save isn't covered by it, so it stays dirty
			// rather than being called saved and then silently lost.
			const current = editor.isDestroyed ? md : getMarkdown(editor);
			setSaveState((s) => (s === 'dirty' && current !== md ? s : 'saved'));
		} catch (err) {
			logger.error('Failed to save markdown doc', err);
			setSaveState('error');
		} finally {
			setSaving(false);
		}
	};

	// Restore the last saved markdown. emitUpdate:false so putting the doc back the way it
	// was doesn't itself register as an unsaved change.
	const cancel = () => {
		if (!editor) return;
		editor.commands.setContent(savedRef.current, { emitUpdate: false });
		setSaveState('idle');
	};

	return (
		<div
			className="glass-card-sm flex min-h-0 flex-1 flex-col"
			data-testid={`${testIdPrefix}-doc`}
		>
			<Toolbar
				editor={editor}
				saveState={saveState}
				saving={saving}
				testIdPrefix={testIdPrefix}
				toolbarAction={toolbarAction}
				onSave={save}
				onCancel={cancel}
			/>
			{header ? <div className="px-4 pt-3">{header}</div> : null}
			<div
				className={cn(
					'scrollbar-hidden min-h-0 flex-1 overflow-y-auto px-4 pt-3 pb-4',
					scrollClassName,
				)}
			>
				<EditorContent editor={editor} />
			</div>
		</div>
	);
}

function Toolbar({
	editor,
	saveState,
	saving,
	testIdPrefix,
	toolbarAction,
	onSave,
	onCancel,
}: {
	editor: Editor | null;
	saveState: SaveState;
	saving: boolean;
	testIdPrefix: string;
	toolbarAction?: ReactNode;
	onSave: () => void;
	onCancel: () => void;
}) {
	const disabled = !editor;
	const pending = saveState === 'dirty' || saveState === 'error';
	// After a failed save the text on screen is the ONLY copy — reverting to the last stored
	// version would destroy it, so Cancel is withheld and only Save (retry) is offered.
	const canCancel = saveState === 'dirty';
	return (
		<div
			className="border-beacon-stroke flex flex-wrap items-center gap-2 border-b
				px-4 py-2"
		>
			<div className="flex items-center gap-1">
				<ActionButton
					isQuiet
					aria-label="Bold"
					isDisabled={disabled}
					onPress={() => editor?.chain().focus().toggleBold().run()}
				>
					<TextBold />
				</ActionButton>
				<ActionButton
					isQuiet
					aria-label="Italic"
					isDisabled={disabled}
					onPress={() => editor?.chain().focus().toggleItalic().run()}
				>
					<TextItalic />
				</ActionButton>
				<ActionButton
					isQuiet
					aria-label="Heading"
					isDisabled={disabled}
					onPress={() =>
						editor?.chain().focus().toggleHeading({ level: 2 }).run()
					}
				>
					H2
				</ActionButton>
				<ActionButton
					isQuiet
					aria-label="Bulleted list"
					isDisabled={disabled}
					onPress={() => editor?.chain().focus().toggleBulletList().run()}
				>
					<ListBulleted />
				</ActionButton>
				<ActionButton
					isQuiet
					aria-label="Numbered list"
					isDisabled={disabled}
					onPress={() => editor?.chain().focus().toggleOrderedList().run()}
				>
					<ListNumbered />
				</ActionButton>
			</div>
			<div className="ml-auto flex items-center gap-3">
				<span data-testid={`${testIdPrefix}-save-state`}>
					<StatusLight
						variant={saving ? 'neutral' : SAVE_VARIANT[saveState]}
						size="S"
					>
						{saving ? 'Saving…' : SAVE_LABEL[saveState]}
					</StatusLight>
				</span>
				<div className="flex items-center gap-2">
					<Button
						variant="secondary"
						size="S"
						isDisabled={disabled || saving || !canCancel}
						onPress={onCancel}
					>
						Cancel
					</Button>
					<Button
						variant="primary"
						size="S"
						isDisabled={disabled || !pending}
						isPending={saving}
						onPress={onSave}
					>
						Save
					</Button>
				</div>
				{toolbarAction}
			</div>
		</div>
	);
}
